import React, { useEffect, useCallback, lazy, Suspense, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, TextField, Select, MenuItem, useMediaQuery, CircularProgress } from '@mui/material';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';
import './OnboardingAITalker.css';
import { showToast } from '../../utils/toastUtils';
import { setBotConfig, setCurrentStep, setOnboardingData, updateVoiceConfig } from '../../redux/onboardingReducer';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';


const VoiceConfig = lazy(() => import('../AITalkerConfiguration/VoiceConfig'));
const MemoizedVoiceConfig = React.memo(VoiceConfig);

const saveAITalkerConfig = async (config, userData) => {
  const accessToken = localStorage.getItem('accessToken');
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/ai-talker/config`, 
      { ...config, userData },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to save AI Talker configuration');
  }
};

const initialState = {
  formData: {
    name: '',
    personalityType: 'Assistant',
  },
  voiceConfig: {},
  profileData: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FORM_DATA':
      return { ...state, formData: { ...state.formData, ...action.payload } };
    case 'SET_VOICE_CONFIG':
      return { ...state, voiceConfig: { ...state.voiceConfig, ...action.payload } };
    case 'SET_PROFILE_DATA':
      return { ...state, profileData: action.payload };
    default:
      return state;
  }
}

const OnboardingAITalker = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const botConfig = useSelector(state => state.onboarding?.botConfig || {});

  const [state, localDispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    localDispatch({ type: 'SET_FORM_DATA', payload: {
      name: botConfig.name || '',
      personalityType: botConfig.personality?.type || 'Assistant',
    }});
    localDispatch({ type: 'SET_VOICE_CONFIG', payload: {
      language: botConfig.voice?.language || '',
      name: botConfig.voice?.name || '',
      pitch: botConfig.voice?.pitch || 0,
      rate: botConfig.voice?.rate || 1,
    }});
  }, [botConfig]);

  useEffect(() => {
    if (location.state && location.state.updatedProfileData) {
      localDispatch({ type: 'SET_PROFILE_DATA', payload: location.state.updatedProfileData });
    } else {
      const storedData = localStorage.getItem('oldUserData');
      if (storedData) {
        localDispatch({ type: 'SET_PROFILE_DATA', payload: JSON.parse(storedData) });
      }
    }
  }, [location]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    localDispatch({ type: 'SET_FORM_DATA', payload: { [name]: value } });
  }, []);

  const handleVoiceConfigChange = useCallback((newVoiceConfig) => {
    localDispatch({ type: 'SET_VOICE_CONFIG', payload: newVoiceConfig });
  }, []);

const saveConfiguration = async (config, showToasts = true) => {
  try {

    const response = await saveAITalkerConfig(config, state.profileData);
    
    if (response && response.message === 'AI Talker configuration saved successfully') {
      // Update Redux store with the latest configuration
      dispatch(setBotConfig(config));
      
      // Combine user profile data and AI Talker configuration
      const combinedData = {
        profileData: state.profileData,
        aiTalkerConfig: config
      };
      
      // Save combined data to Redux store
      dispatch(setOnboardingData(combinedData));

      if (showToasts) {
        showToast('AI Talker configuration saved successfully', 'success');
      }
      return true;
    } else {
      throw new Error(response?.message || 'Unexpected response from server');
    }
  } catch (error) {
    if (showToasts) {
      showToast(`Failed to save AI Talker configuration: ${error.message}`, 'error');
    }
    return false;
  }
};

const handleVoicePreview = useCallback(async (previewConfig) => {
  dispatch(updateVoiceConfig(previewConfig));
  localDispatch({ type: 'SET_VOICE_CONFIG', payload: previewConfig });
  
  const configToSave = {
    ...state.formData,
    voice: {
      ...previewConfig,
      name: previewConfig.name || previewConfig.voice || ''
    },
    personality: {
      type: state.formData.personalityType
    }
  };
  
  await saveConfiguration(configToSave, false);
}, [dispatch, state.formData]);


const handleSubmit = async () => {
  // Validate required fields
  if (!state.formData.name || !state.formData.personalityType || !state.voiceConfig.name) {
    showToast('Please fill in all required fields.', 'error');
    return;
  }

  const configToSave = {
    ...state.formData,
    voice: {
      ...state.voiceConfig,
      name: state.voiceConfig.name || state.voiceConfig.voice || ''
    },
    personality: {
      type: state.formData.personalityType
    }
  };

  const success = await saveConfiguration(configToSave, true);
};

  return (
    <Box className="container fade-in">
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom className="title bounce-in">
        Design Your AI Talker
      </Typography>

      <Box className="formSection slide-in-left">
        <Typography variant={isMobile ? "body1" : "subtitle1"} gutterBottom>AI Talker Name</Typography>
        <TextField
          fullWidth
          name="name"
          value={state.formData.name}
          onChange={handleChange}
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          className="hover-effect"
        />
      </Box>

      <Box className="formSection slide-in-right">
        <Typography variant={isMobile ? "body1" : "subtitle1"} gutterBottom>Personality Type</Typography>
        <Select
          fullWidth
          name="personalityType"
          value={state.formData.personalityType}
          onChange={handleChange}
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          className="hover-effect"
        >
          <MenuItem value="Assistant">Assistant</MenuItem>
          <MenuItem value="Expert">Expert</MenuItem>
          <MenuItem value="Friend">Friend</MenuItem>
          <MenuItem value="Professional">Professional</MenuItem>
        </Select>
      </Box>

      <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom className="voiceConfigTitle slide-in-left">
        Voice Configuration
      </Typography>

      <Suspense fallback={<CircularProgress />}>
        <div className="voice-config-wrapper slide-in-right">
          <MemoizedVoiceConfig
            initialConfig={state.voiceConfig}
            onChange={handleVoiceConfigChange}
            onPreview={handleVoicePreview}
            isOnboarding={true}
            isMobile={isMobile}
          />
        </div>
      </Suspense>

      <OnboardingButtonDock
        onSave={handleSubmit}
        onBack={() => {
          dispatch(setCurrentStep(1)); // Go back to Use Case
        }}
        onNext={() => {
          handleSubmit().then(() => {
            dispatch(setCurrentStep(3)); // Move to Playground
          });
        }}
        title="save configuration"
        loading={false}
        desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
        mobileClassName="fixed bottom-8 right-8 z-50"
      />
    </Box>
  );
};

export default OnboardingAITalker;

